import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress,
  Alert
} from '@mui/material';
import { ChatType } from '../../types/chat';
import { createChat } from '../../services/chatService';
import { useNavigate } from 'react-router-dom';

interface NewChatFormProps {
  initialMessage?: string;
  chatType?: 'car_listing' | 'vin_analysis' | 'general';
}

const NewChatForm: React.FC<NewChatFormProps> = ({ 
  initialMessage,
  chatType = 'general'
}) => {
  const [chatName, setChatName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Set default chat name based on type
  useEffect(() => {
    switch (chatType) {
      case 'car_listing':
        setChatName('Car Listing Analysis');
        break;
      case 'vin_analysis':
        setChatName('VIN Analysis');
        break;
      default:
        setChatName('My Next Car Chat');
        break;
    }
  }, [chatType]);
  
  const validateChat = () => {
    if (!chatName.trim()) {
      setError('Please enter a chat name');
      return false;
    }
    return true;
  };
  
  const handleCreateChat = async () => {
    if (!validateChat()) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await createChat({
        name: chatName,
        type: ChatType.GENERAL
      });
      
      setSuccess('Chat created successfully!');
      
      setTimeout(() => {
        // Navigate directly to streaming chat with initial message if provided
        if (initialMessage) {
          navigate(`/streaming-chat/${response.id}`, {
            state: { initialMessage },
            replace: false // Ensure the state is preserved in history
          });
        } else {
          navigate(`/streaming-chat/${response.id}`);
        }
      }, 500); // Reduced delay to 500ms
    } catch (err: any) {
      console.error('Error creating chat:', err);
      setError('Failed to create chat. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        borderRadius: 2, 
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        mb: 4
      }}
    >
      <Box sx={{ p: 3 }}>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        
        {success && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success">{success}</Alert>
          </Box>
        )}
        
        <Typography variant="h6" gutterBottom>
          {chatType === 'general' ? 'Start a New Chat' : 'Ready to Analyze'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {chatType === 'general' 
            ? 'Chat with the assistant about any car-related questions you have.'
            : 'Your analysis request is ready. Click "Start Analysis" to begin.'
          }
        </Typography>

        {initialMessage && (
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Your Request:
            </Typography>
            <Typography variant="body2">
              {initialMessage}
            </Typography>
          </Paper>
        )}
        
        <TextField
          fullWidth
          label="Chat Name"
          variant="outlined"
          value={chatName}
          onChange={(e) => setChatName(e.target.value)}
          sx={{ 
            mb: 3,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.02)',
              },
              '&.Mui-focused': {
                transform: 'scale(1.02)',
              }
            }
          }}
        />
        
        <Button 
          variant="contained" 
          fullWidth 
          onClick={handleCreateChat}
          disabled={loading}
          sx={{
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: (theme) => theme.shadows[8],
            },
            '&:active': {
              transform: 'translateY(0px)',
            }
          }}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : (
            initialMessage ? 'Start Analysis' : 'Start Chat'
          )}
        </Button>
      </Box>
    </Paper>
  );
};

export default NewChatForm;
